<script setup lang="ts">
import Button from '@/components/button-primary.vue'
import { computed, inject, ref } from 'vue'
import { startOAuth } from '@/features/auth/api/startOAuth'
import { useRoute } from 'vue-router'
import { exchangeOAuthCodeForToken } from '@/features/auth/api/exchangeOAuthCodeForToken'
import { navigateTo } from '@/helpers/routing'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import IconLoading from '@/shared/components/IconLoadingSpinner.vue'
import IconLoadingSpinner from '@/shared/components/IconLoadingSpinner.vue'
import { UpdateShowTermsInjectionKey } from '@/features/auth/injections'
import IconError from '@/shared/components/IconError.vue'
import ErrorBlock from '@/shared/components/ErrorBlock.vue'
import { ValidationError } from '@/shared/api/validation'

const route = useRoute()
const failureReason = ref<string>()
const currentState = ref<'starting'|'completing'|'error'|undefined>()
const currentStateDetail = ref<string>()
const localStorageKey = 'auth.continuationUrl'
const showTerms = inject(UpdateShowTermsInjectionKey)

// We've returned from the OAuth flow, and we need to exchange for a token.
if (typeof route.query.code === 'string') {
  showTerms?.(false)
  currentState.value = 'completing'
  currentStateDetail.value = 'Completing authentication...'

  exchangeOAuthCodeForToken(route.query.code).then(
    () => {
      const url = window.localStorage.getItem(localStorageKey) ?? undefined
      window.localStorage.removeItem(localStorageKey)

      currentStateDetail.value = 'Successfully authenticated! Redirecting...'

      if (url) {
        window.location.href = url
      } else {
        navigateTo('projects.list')
      }
    },
    (why: unknown) => {
      showTerms?.(true)
      currentState.value = 'error'

      let errorMessage = `Authentication failed due to an unknown reason. Please try again.`

      if (why instanceof ValidationError) {
        const isCodeError = !!why.errors.find(x => x.pointer === 'code')

        if (isCodeError) {
          errorMessage = 'Unable to complete authentication. Please try again.'
        }
      }

      currentStateDetail.value = errorMessage
    }
  )
}

const startSignIn = () => {
  currentState.value = 'starting'

  // Store/remove the continuation URL.
  if (typeof route.query.next === 'string') {
    window.localStorage.setItem(localStorageKey, route.query.next)
  } else {
    window.localStorage.removeItem(localStorageKey)
  }

  startOAuth().then(
    ({ data: { redirectUrl } }) => {
      window.location.href = redirectUrl
    },
    () => {
      showTerms?.(true)

      currentState.value = 'error'
      currentStateDetail.value = `Authentication could not be started. Please try again.`
    }
  )
}
</script>

<template>
  <h3 class="mt-4 mb-4">{{ currentState === 'completing' ? 'Signing' : 'Please sign' }} in to continue with your awesome work...</h3>

  <div :class="$style['wrapper']" class="ms-auto me-auto">
    <Button v-if="[undefined, 'error', 'starting'].includes(currentState)" :class="$style['sign-in']" @click="startSignIn" :state="currentState === 'starting' ? 'pending' : undefined" icon-position="before">
      <svg v-if="currentState !== 'starting'" :class="$style['microsoft-icon']" class="me-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19 19">
        <rect x="0" y="0" width="9" height="9" fill="#f25022"/>
        <rect x="0" y="10" width="9" height="9" fill="#00a4ef"/>
        <rect x="10" y="0" width="9" height="9" fill="#7fba00"/>
        <rect x="10" y="10" width="9" height="9" fill="#ffb900"/>
      </svg>

      <span :class="$style['button-text']">Sign in with Microsoft</span>
    </Button>

    <ErrorBlock v-if="currentStateDetail && currentState === 'error'" layout="horizontal" :class="$style['detail-error']">{{ currentStateDetail }}</ErrorBlock>

    <div v-if="currentState === 'completing'">
      <IconLoadingSpinner />
      <span class="ms-2 text-secondary">Completing authentication...</span>
    </div>
  </div>
</template>

<style module lang="scss">
@use "sass:map";
@import "@/assets/variables";

.detail-completing {
  margin-top: #{$spacer};
  > span {
    margin-left: #{map.get($spacers, 2)};
    color: $secondary;
  }
}

.detail-error {
  border: 1px solid #{$warning-border-subtle};
  border-radius: 4px;
  margin-top: #{$spacer};
  padding: 1rem;
  background: $warning-bg-subtle;

  > span {
    display: block;
    color: $warning;
  }
}

.wrapper {
  width: 276px;
}

.microsoft-icon {
  width: var(--#{$prefix}btn-font-size);
  height: var(--#{$prefix}btn-font-size);
  vertical-align: -0.125em;  /* Matches that of Font Awesome */
}
</style>
